import React, { useState } from "react";
import { Box, InputBase, Typography, CircularProgress } from "@mui/material";
import { Search, ArrowRight, BookOpen, Briefcase, FileText, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { unifiedSearch } from "../../api/search";

const RESULT_META = {
  courses: { label: "Course", icon: BookOpen, color: "#2563EB", to: () => "/learn" },
  services: { label: "Service", icon: Briefcase, color: "#0891B2", to: () => "/business" },
  resources: { label: "Resource", icon: FileText, color: "#7C3AED", to: () => "/resources" },
};

export default function SearchAssistant() {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ["search", q],
    queryFn: () => unifiedSearch(q).then((r) => r.data),
    enabled: q.length >= 2,
  });

  const groups = [
    { key: "courses", items: data?.courses, titleKey: "title" },
    { key: "services", items: data?.services, titleKey: "name" },
    { key: "resources", items: data?.resources, titleKey: "title" },
  ].filter((g) => g.items?.length);

  const hasResults = groups.length > 0;
  const showPanel = q.length >= 2;
  const totalCount = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <Box
      component="section"
      id="search"
      sx={{
        position: "relative",
        bgcolor: "#FAFBFF",
        py: { xs: 8, md: 11 },
        overflow: "hidden",
      }}
    >
      {/* Faint blueprint grid backdrop, consistent with the architecture section */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(29,78,216,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(29,78,216,0.045) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse 60% 60% at 50% 0%, black 0%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 60% at 50% 0%, black 0%, transparent 75%)",
        }}
      />

      <div className="section-shell" style={{ position: "relative" }}>
        <Box sx={{ maxWidth: 640, mx: "auto", textAlign: "center", mb: 3.5 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#2563EB",
              mb: 1,
            }}
          >
            Quick search
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em", fontSize: { xs: "1.35rem", sm: "1.6rem" } }}
          >
            Find a course, service, or resource in seconds.
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 640, mx: "auto" }}>
          {/* Search field */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              bgcolor: "white",
              borderRadius: showPanel ? "16px 16px 0 0" : "16px",
              border: "1px solid",
              borderColor: focused ? "#2563EB" : "rgba(15,23,42,0.10)",
              boxShadow: focused
                ? "0 0 0 4px rgba(37,99,235,0.10), 0 1px 2px rgba(15,23,42,0.04)"
                : "0 1px 3px rgba(15,23,42,0.05)",
              px: 2,
              py: 1.4,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease, border-radius 0.15s ease",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", color: focused ? "#2563EB" : "#94A3B8", flexShrink: 0 }}>
              {isFetching ? <CircularProgress size={17} thickness={5} sx={{ color: "#2563EB" }} /> : <Search size={18} />}
            </Box>
            <InputBase
              fullWidth
              placeholder="Search courses, services, resources…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              sx={{
                fontSize: 14.5,
                fontWeight: 500,
                "& input::placeholder": { color: "#94A3B8", opacity: 1 },
              }}
            />
            {q.length > 0 && (
              <Box
                role="button"
                aria-label="Clear search"
                onClick={() => setQ("")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  color: "#94A3B8",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                  "&:hover": { bgcolor: "#F1F5F9", color: "#475569" },
                }}
              >
                <X size={14} />
              </Box>
            )}
          </Box>

          {/* Results panel */}
          {showPanel && (
            <Box
              sx={{
                bgcolor: "white",
                border: "1px solid rgba(15,23,42,0.10)",
                borderTop: "1px solid rgba(15,23,42,0.06)",
                borderRadius: "0 0 16px 16px",
                boxShadow: "0 12px 28px -12px rgba(15,23,42,0.14)",
                overflow: "hidden",
              }}
            >
              {isFetching && !data ? (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1.25} py={4}>
                  <CircularProgress size={16} thickness={5} sx={{ color: "#2563EB" }} />
                  <Typography variant="body2" color="text.secondary">
                    Searching…
                  </Typography>
                </Box>
              ) : !hasResults ? (
                <Box sx={{ textAlign: "center", py: 4, px: 3 }}>
                  <Typography variant="body2" fontWeight={600} color="#334155">
                    No results for "{q}"
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Try a different keyword or check the spelling.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
                  {groups.map(({ key, items, titleKey }, gi) => {
                    const meta = RESULT_META[key];
                    const Icon = meta.icon;
                    return (
                      <Box
                        key={key}
                        sx={{ borderTop: gi === 0 ? "none" : "1px solid rgba(15,23,42,0.05)" }}
                      >
                        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              letterSpacing: "0.07em",
                              textTransform: "uppercase",
                              color: "#94A3B8",
                            }}
                          >
                            {meta.label}s
                          </Typography>
                        </Box>
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            component={Link}
                            to={meta.to()}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mx: 1,
                              px: 1.25,
                              py: 1.1,
                              borderRadius: "10px",
                              textDecoration: "none",
                              color: "inherit",
                              transition: "background-color 0.15s ease",
                              "&:hover": { bgcolor: "rgba(37,99,235,0.045)" },
                              "&:hover .search-arrow": { transform: "translateX(2px)", color: meta.color },
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                bgcolor: `${meta.color}14`,
                                color: meta.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Icon size={15} strokeWidth={2.25} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  color: "#0F172A",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item[titleKey]}
                              </Typography>
                            </Box>
                            <ArrowRight
                              className="search-arrow"
                              size={15}
                              style={{ color: "#CBD5E1", flexShrink: 0, transition: "transform 0.15s ease, color 0.15s ease" }}
                            />
                          </Box>
                        ))}
                      </Box>
                    );
                  })}

                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderTop: "1px solid rgba(15,23,42,0.05)",
                      bgcolor: "#F8FAFF",
                    }}
                  >
                    <Typography sx={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 500 }}>
                      {totalCount} result{totalCount !== 1 ? "s" : ""} for "{q}"
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </div>
    </Box>
  );
}